import classNames from 'classnames';
import { Button } from '@praktio/common-ui';
import { debounce, difference } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import LoadDateTime from '../LoadDateTime/LoadDateTime';
import IconTooltip from '../../../ui/IconTooltip/IconTooltip';
import IconMaterial from '../../../ui/IconMaterial/IconMaterial';
import ActiveCourses from '../../../common/ActiveCourses/ActiveCourses';
import ActiveTeamsTree from '../../../common/ActiveTeamsTree/ActiveTeamsTree';

import { RootState } from '../../../../ducks/store';
import { useActiveTeams } from '../../../../hooks/useActiveTeams';
import { toggleReportSettingsPane } from '../../../../ducks/report';
import { useGetTeamsSlice } from '../../../../hooks/useGetTeamsSlice';
import { useGetCourseNames } from '../../../../hooks/useGetCourseNames';
import { useGetTeamCourses } from '../../../../hooks/useGetTeamCourses';
import { useActiveDeselectedCourses } from '../../../../hooks/useActiveDeselectedCourses';
import {
  toggleSelection,
  getCourseIdsFromCourseNames,
  showSearchBar,
  getActiveTeams,
} from '../../../../utils/reports';

import {
  buildTeamHierarchy,
  DEBOUNCE_DELAY,
  hasTeamManagerRole,
  teamsOrDeselectedCoursesHaveChanged,
} from '../../../../utils';

import './ReportSettingsPane.scss';

interface Props {
  teams?: ITeam[];
}

const ReportSettingsPane = ({ teams = [] }: Props) => {
  // ===========================================================================
  // RTK Query
  // ===========================================================================

  const { fetchCourses, courses, error, isLoading, isFetching } = useGetCourseNames();

  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const { activeTeams, reportSettingsPaneOpen } = useSelector((state: RootState) => {
    const user = state.auth.authenticatedUser;
    const isTeamManager = hasTeamManagerRole(user);
    const activeTeams = getActiveTeams(user, isTeamManager);

    return {
      activeTeams,
      reportSettingsPaneOpen: state.report.reportSettingsPaneOpen,
    };
  });

  // ===========================================================================
  // Hooks
  // ===========================================================================

  const { getTeamsCourseIds } = useGetTeamCourses();
  const { setActiveTeams, storeTeams } = useActiveTeams();

  const { currentTeamCourseIds, setCurrentTeamCourseIds, setActiveCourseNames } =
    useGetTeamsSlice();

  const { activeDeselectedCourses, setActiveDeselectedCourses, storeDeselectedCourses } =
    useActiveDeselectedCourses();

  // ===========================================================================
  // State
  // ===========================================================================

  const [teamSelectionLoading, setTeamSelectionLoading] = useState(false); // Used to show loading spinner when selecting teams
  const [selectedTeams, setSelectedTeams] = useState<string[]>(activeTeams);
  const [selectedTeamsPrev, setSelectedTeamsPrev] = useState<string[]>(activeTeams);
  const [deselectedCourses, setDeselectedCourses] = useState<string[]>(activeDeselectedCourses);
  const [currentTeamCourseIdsLocal, setCurrentTeamCourseIdsLocal] =
    useState<string[]>(currentTeamCourseIds);

  // ===========================================================================
  // Other
  // ===========================================================================

  const treeData = buildTeamHierarchy({ teams, sorted: true }); // Format teams to display in ActiveTeamsTree
  const isLoadingOrFetching = isLoading || isFetching;

  const noCoursesSelected = currentTeamCourseIdsLocal.every((courseId) =>
    deselectedCourses.includes(courseId),
  );

  const isSaveDisabled = !selectedTeams.length || noCoursesSelected || teamSelectionLoading;

  const hasTeamsOrCoursesChanged = teamsOrDeselectedCoursesHaveChanged(
    activeTeams,
    activeDeselectedCourses,
    selectedTeams,
    deselectedCourses,
  );

  const tooltipMessage = isSaveDisabled
    ? 'Please select at least one team and course.'
    : !hasTeamsOrCoursesChanged
    ? 'No changes to save'
    : '';

  // ===========================================================================
  // Debounced Functions
  // - We use these debounced functions to reduce the number of requests made
  //   to the server to get report data.
  // ===========================================================================

  const debouncedSetActiveTeams = useRef(
    debounce((activeTeams) => {
      storeTeams(activeTeams);
    }, DEBOUNCE_DELAY),
  ).current;

  const debouncedSetActiveDeselectedCourses = useRef(
    debounce(async (activeDeselectedCourses) => {
      await setActiveDeselectedCourses(activeDeselectedCourses);
      storeDeselectedCourses(activeDeselectedCourses);
    }, DEBOUNCE_DELAY),
  ).current;

  const debouncedFetchCourses = useRef(
    debounce((teamIds) => {
      fetchCourses({ teamIds }).then(({ data: courses }) => {
        const courseIds = getCourseIdsFromCourseNames(courses);

        setCurrentTeamCourseIdsLocal(courseIds);
        setTeamSelectionLoading(false);
      });
    }, DEBOUNCE_DELAY),
  ).current;

  // ===========================================================================
  // Functions
  // ===========================================================================

  /**
   * Automatically select team courses when a team or teams are selected.
   * @param selectedTeams  - Array of selected team ids
   */
  const autoSelectTeamCourses = async (selectedTeams: string[]) => {
    setTeamSelectionLoading(true);

    // If no teams are selected, clear selected courses
    if (!selectedTeams.length) {
      setDeselectedCourses([]);
      return;
    }

    const addedTeams = difference(selectedTeams, selectedTeamsPrev); // Teams that were added
    const newTeamCoursesIds = getTeamsCourseIds(addedTeams); // Course IDs for newly selected teams only
    const newDeselectedCourses = difference(deselectedCourses, newTeamCoursesIds); // Remove IDs from deselected courses if they are in the new team courses

    setDeselectedCourses(newDeselectedCourses);
  };

  /**
   * Get courses for selected teams.
   */
  const getCourses = (selectedTeams: string[]) => {
    if (!selectedTeams.length) {
      debouncedFetchCourses([]); // Clear courses if no teams are selected
      return;
    }

    debouncedFetchCourses(selectedTeams);
  };

  const togglePane = () => {
    dispatch(toggleReportSettingsPane());
  };

  const updateActiveCourseNames = () => {
    const courseNames =
      courses
        ?.filter((course) => !deselectedCourses.includes(course._id))
        .map((course) => course.title) || [];

    setActiveCourseNames(courseNames);
  };

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleSelectedTeamsChange = (selectedTeams: string[]) => {
    setSelectedTeams(selectedTeams);
    setSelectedTeamsPrev(selectedTeams);
    autoSelectTeamCourses(selectedTeams);
  };

  const handleCourseClick = (courseId: string) => {
    setDeselectedCourses((prev) => toggleSelection(prev, courseId));
  };

  const handleClearAllCourses = () => {
    const allCourseIds = getCourseIdsFromCourseNames(courses);
    setDeselectedCourses(allCourseIds);
  };

  const handleSelectAllCourses = () => setDeselectedCourses([]);

  const handleOnSave = () => {
    setActiveTeams(selectedTeams);
    setActiveDeselectedCourses(deselectedCourses);
    setCurrentTeamCourseIds(currentTeamCourseIdsLocal);

    debouncedSetActiveTeams(selectedTeams);
    debouncedSetActiveDeselectedCourses(deselectedCourses);

    updateActiveCourseNames();
  };

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  useEffect(() => {
    setActiveTeams(selectedTeams);
    setActiveDeselectedCourses(deselectedCourses);

    getCourses(selectedTeams);

    // We don't want to run this effect when getCourses, selectedTeams or selectedCourses changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, dispatch]);

  useEffect(() => {
    getCourses(selectedTeams);

    // We don't want to run this effect when getCourses changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeams, dispatch]);

  // ===========================================================================
  // Render
  // ===========================================================================

  const renderCourses = () => {
    const btnHandler = noCoursesSelected ? handleSelectAllCourses : handleClearAllCourses;

    return (
      <ActiveCourses
        noSubheader
        className="ReportSettingsPane_Courses"
        courses={courses}
        deselectedCourses={deselectedCourses}
        noCoursesSelected={noCoursesSelected}
        error={error as FetchBaseQueryError}
        isLoading={isLoadingOrFetching || teamSelectionLoading}
        onClearSelectClick={btnHandler}
        onCourseClick={handleCourseClick}
      />
    );
  };

  return (
    <>
      <button
        className={classNames('ReportSettingsPane_Btn', {
          'ReportSettingsPane_Btn--hide': !!reportSettingsPaneOpen,
        })}
        onClick={togglePane}
      >
        <IconMaterial name="tune" />
      </button>

      <div
        className={classNames('ReportSettingsPane_Pane', {
          'ReportSettingsPane_Pane--open': reportSettingsPaneOpen,
        })}
      >
        <header>
          <div>
            <h2>Report Settings</h2>

            <IconTooltip
              id="tooltip-courses"
              icon="help"
              placement="right"
              text="Select which teams and courses appear in your reports."
            />
          </div>

          <button onClick={togglePane}>
            <IconMaterial name="close" />
          </button>
        </header>

        {treeData ? (
          <ActiveTeamsTree
            noDefault
            showTooltip
            isSearchable
            isLocked={isLoadingOrFetching}
            teams={teams}
            activeTeams={selectedTeams}
            subHeader="Teams"
            onSelectedChange={handleSelectedTeamsChange}
          />
        ) : (
          <p>No teams found.</p>
        )}

        <hr />

        {renderCourses()}

        <hr />

        <footer>
          <LoadDateTime />

          <Button
            disabled={isSaveDisabled || !hasTeamsOrCoursesChanged}
            tooltip={tooltipMessage}
            tooltipPlacement="right"
            onClick={handleOnSave}
          >
            Save
          </Button>
        </footer>
      </div>
    </>
  );
};

export default ReportSettingsPane;
